import { useEffect, useRef, useState } from "react";

/**
 * Renders a locally-hosted handwritten note PNG with **automatic
 * white-margin cropping** so the content fills the frame.
 *
 * (Earlier versions also accepted a Google Drive `fileId`. Drive
 * integration has been retired — this component is now local-only.)
 */

export interface NoteImageProps {
  /** Local public/ path, e.g. /notes-book/music-theory/1/page-1.png */
  src: string;
  alt?: string;
  /** Background-pixel threshold (0-255). Pixels brighter than this are paper. */
  whiteThreshold?: number;
  /** Padding (image px) to keep around the detected content bbox. */
  padding?: number;
}

export function NoteImage({ src, alt = "Lesson notes", whiteThreshold = 240, padding = 24 }: NoteImageProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "fallback">("loading");

  useEffect(() => {
    let cancelled = false;
    setState("loading");
    if (!src) return;

    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (cancelled) return;
      try {
        const W = img.naturalWidth;
        const H = img.naturalHeight;
        const probe = document.createElement("canvas");
        const probeScale = Math.min(1, 1000 / Math.max(W, H));
        const pw = Math.max(1, Math.round(W * probeScale));
        const ph = Math.max(1, Math.round(H * probeScale));
        probe.width = pw;
        probe.height = ph;
        const pctx = probe.getContext("2d", { willReadFrequently: true })!;
        pctx.drawImage(img, 0, 0, pw, ph);
        const { data } = pctx.getImageData(0, 0, pw, ph);

        let minX = pw, minY = ph, maxX = 0, maxY = 0;
        for (let y = 0; y < ph; y++) {
          for (let x = 0; x < pw; x++) {
            const i = (y * pw + x) * 4;
            const r = data[i], g = data[i + 1], b = data[i + 2];
            if (r < whiteThreshold || g < whiteThreshold || b < whiteThreshold) {
              if (x < minX) minX = x;
              if (x > maxX) maxX = x;
              if (y < minY) minY = y;
              if (y > maxY) maxY = y;
            }
          }
        }

        if (maxX < minX || maxY < minY) {
          setState("fallback");
          return;
        }

        const sx = Math.max(0, Math.round(minX / probeScale) - padding);
        const sy = Math.max(0, Math.round(minY / probeScale) - padding);
        const sw = Math.min(W - sx, Math.round((maxX - minX + 1) / probeScale) + padding * 2);
        const sh = Math.min(H - sy, Math.round((maxY - minY + 1) / probeScale) + padding * 2);

        const out = canvasRef.current;
        if (!out) return;
        out.width = sw;
        out.height = sh;
        const octx = out.getContext("2d")!;
        octx.drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
        setState("ready");
      } catch {
        setState("fallback");
      }
    };
    img.onerror = () => {
      if (!cancelled) setState("fallback");
    };

    return () => { cancelled = true; };
  }, [src, whiteThreshold, padding]);

  if (state === "fallback") {
    return <img src={src} alt={alt} loading="lazy" className="h-auto w-full bg-white" />;
  }

  return (
    <div className="relative w-full">
      <canvas
        ref={canvasRef}
        aria-label={alt}
        className="h-auto w-full bg-white"
        style={{ display: state === "ready" ? "block" : "none" }}
      />
      {state === "loading" && (
        <div className="flex aspect-[4/3] w-full items-center justify-center bg-zinc-100 text-xs text-zinc-500">
          Loading note…
        </div>
      )}
    </div>
  );
}
